import { useContext, useEffect, useState } from 'react';
import { Box, Button, Chip, Sheet, Stack, Typography } from '@mui/joy';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';

import { LayoutContext } from '../context';
import { APIDateTreatStatus } from '../api_date_treat';
import DateTreatPickerModal from './DateTreatPickerModal';

// Мягкая подсказка показывается только когда сертификат ещё можно заслужить.
const REASON_HINT = {
    no_match: 'Сертификат станет доступен после первой взаимной симпатии.',
};

export default function DateTreatBanner() {
    const { token } = useContext(LayoutContext);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pickerOpen, setPickerOpen] = useState(false);

    useEffect(() => {
        let alive = true;
        if (!token) { setLoading(false); return; }
        APIDateTreatStatus(token)
            .then((r) => { if (alive) setData(r); })
            .catch(() => {})
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [token]);

    if (loading || !data || !data.offer?.enabled) return null;

    const treat = data.treat;

    // Уже есть активный сертификат.
    if (treat && treat.status === 'active') {
        return (
            <Sheet variant="soft" color="primary" sx={{ p: 2, borderRadius: 'lg', mb: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <FavoriteRoundedIcon color="primary" />
                    <Box sx={{ flex: 1 }}>
                        <Typography level="title-md">Свидание за наш счёт</Typography>
                        <Typography level="body-sm" color="neutral">
                            Сертификат на {Math.round(treat.amount)} ₽ — покажите код на месте.
                        </Typography>
                    </Box>
                    <Chip size="lg" variant="solid" color="primary">{treat.code}</Chip>
                </Stack>
                {treat.place && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        <PlaceRoundedIcon fontSize="small" color="primary" />
                        <Typography level="body-sm">
                            {treat.place.name}
                            {treat.place.address ? `, ${treat.place.address}` : ''}
                            {treat.place.category ? ` · ${treat.place.category}` : ''}
                        </Typography>
                    </Stack>
                )}
                {treat.expires_at && (
                    <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
                        Действует до {new Date(treat.expires_at).toLocaleDateString('ru-RU')}.
                    </Typography>
                )}
            </Sheet>
        );
    }

    // Доступен к получению — запускаем выбор места.
    if (data.eligible) {
        return (
            <>
                <Sheet variant="soft" color="primary" sx={{ p: 2, borderRadius: 'lg', mb: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <FavoriteRoundedIcon color="primary" />
                        <Box sx={{ flex: 1 }}>
                            <Typography level="title-md">Первое свидание за наш счёт</Typography>
                            <Typography level="body-sm" color="neutral">
                                Выберите место — и заберите сертификат на {Math.round(data.offer.amount)} ₽.
                            </Typography>
                        </Box>
                        <Button onClick={() => setPickerOpen(true)}>Выбрать место</Button>
                    </Stack>
                </Sheet>
                <DateTreatPickerModal
                    open={pickerOpen}
                    onClose={() => setPickerOpen(false)}
                    token={token}
                    amount={data.offer.amount}
                    onClaimed={(newTreat) => {
                        setData((d) => ({ ...d, treat: newTreat, eligible: false }));
                        setPickerOpen(false);
                    }}
                />
            </>
        );
    }

    // Недоступен — мягкая подсказка.
    const hint = REASON_HINT[data.reason];
    if (!hint) return null;
    return (
        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'lg', mb: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
                <FavoriteRoundedIcon color="neutral" />
                <Box>
                    <Typography level="title-sm">Свидание за наш счёт</Typography>
                    <Typography level="body-sm" color="neutral">{hint}</Typography>
                </Box>
            </Stack>
        </Sheet>
    );
}